import { adminMutate, adminUpload, apiRead, type ApiOptions } from '../admin/primitives.js'
import {
  erreurReseau, lireActions, lireAffiche, lireEcriture, lireErreur, lireFiche, lireListe, lireSalles,
  type Actions, type Affiche, type Ecriture, type ErreurBM, type Fiche, type ReponseListe, type Salle,
} from './soirees-maison-model.js'
import {
  lirePreferences, lireReglages, type ChangementReglage, type CorpsPreferences, type ModeleTarif, type Preferences, type ReglageBilletterie,
} from './reglages-billetterie-model.js'
import type { CorpsCreation } from './assistant-soiree-model.js'
import type { CorpsTarif } from './tarif-saisie-model.js'

/**
 * SOIRÉES DE BILLETTERIE LIVE CLUB — LE SEUL MODULE QUI PARLE AU SERVEUR.
 *
 * Tout passe par l'API de GESTION (`/api/v1/billetterie/…`, contrat
 * `soirees-v1` §6.3), qui vérifie les droits, le gel et le Mode sûr, construit
 * l'acteur et relaie vers la billetterie. Le navigateur n'envoie que le corps
 * du contrat et la clé d'idempotence du geste.
 *
 * Aucun écran n'appelle `fetch` lui-même : tout rend un `Resultat`, jamais
 * d'exception. Aucune écriture n'est réessayée seule : c'est la personne qui
 * recommence, avec la MÊME clé si le corps n'a pas changé (`GardeCles`).
 *
 * `deconnexionExplicite` : seul un 401 qui dit être la fin de la session
 * GESTION déconnecte ; un refus de la billetterie ne fait jamais sortir de
 * Gestion (le client le rend en 503 « jeton refusé », jamais en 401).
 */

const BASE = '/api/v1/billetterie'
const OPTIONS: ApiOptions = { deconnexionExplicite: true }

export type Resultat<T> = { ok: true; status: number; data: T } | { ok: false; erreur: ErreurBM }

/** Un identifiant de chemin validé AVANT l'appel (§6.3) : jamais un segment douteux. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
function id(x: string): string | null {
  return UUID.test(x) ? x.toLowerCase() : null
}

const introuvable = (): Resultat<never> => ({
  ok: false,
  erreur: { genre: 'introuvable', code: 'BILLETTERIE_INTROUVABLE', status: 404, message: 'Cette adresse ne désigne aucune soirée.', champs: [], version: null, ambiguites: [], traceId: null },
})

const formeInattendue = (status: number, ecriture: boolean): Resultat<never> => ({
  ok: false,
  erreur: {
    genre: 'autre', code: 'FORME_INATTENDUE', status,
    message: ecriture
      ? 'Le geste a été accepté, mais la réponse n’a pas la forme attendue : rechargez la soirée pour voir son état réel.'
      : 'La réponse n’a pas la forme attendue : rien n’est affiché plutôt qu’une donnée douteuse.',
    champs: [], version: null, ambiguites: [], traceId: null,
  },
})

async function lire<T>(chemin: string, interpreter: (v: unknown) => T | null): Promise<Resultat<T>> {
  try {
    const r = await apiRead<unknown>(chemin, OPTIONS)
    const e = lireErreur(r.status, r.data)
    if (e) return { ok: false, erreur: e }
    const v = interpreter(r.data)
    return v === null ? formeInattendue(r.status, false) : { ok: true, status: r.status, data: v }
  } catch {
    return { ok: false, erreur: erreurReseau(false) }
  }
}

async function ecrire<T>(methode: 'POST' | 'PUT', chemin: string, corps: unknown, cle: string, interpreter: (v: unknown) => T | null): Promise<Resultat<T>> {
  try {
    const r = await adminMutate<{ error?: string }>(methode, chemin, corps, { ...OPTIONS, headers: { 'Idempotency-Key': cle } })
    const e = lireErreur(r.status, r.data)
    if (e) return { ok: false, erreur: e }
    const v = interpreter(r.data)
    return v === null ? formeInattendue(r.status, true) : { ok: true, status: r.status, data: v }
  } catch {
    return { ok: false, erreur: erreurReseau(true) }
  }
}

/* ═══════════════════════ lectures ═══════════════════════ */

/** `q` : les clés de la liste blanche seulement (`requeteListe`). */
export function chargerListe(q: Record<string, string>): Promise<Resultat<ReponseListe>> {
  const p = new URLSearchParams(q)
  return lire(`${BASE}/soirees${p.toString() ? `?${p.toString()}` : ''}`, lireListe)
}

export function chargerFiche(soireeId: string): Promise<Resultat<Fiche>> {
  const s = id(soireeId)
  return s ? lire(`${BASE}/soirees/${s}`, lireFiche) : Promise.resolve(introuvable())
}

export function chargerSalles(): Promise<Resultat<Salle[]>> {
  return lire(`${BASE}/salles`, lireSalles)
}

export interface ReponseReglages { reglages: ReglageBilletterie[] }
export function chargerReglages(): Promise<Resultat<ReponseReglages>> {
  return lire(`${BASE}/reglages`, (v) => {
    const r = lireReglages(v)
    return r ? { reglages: r } : null
  })
}

/** Les réglages de Gestion, et ce que le serveur dit autour : gel illisible, Mode sûr, défauts, droits, écriture. */
export interface ReponsePreferences {
  preferences: Preferences
  gel_illisible: boolean
  /** L'heure de la ligne lue : renvoyée en `maj_a_lue`, un enregistrement d'après un écran périmé est refusé. */
  maj_a: string | null
  mode_sur: { libelle: string; message: string | null } | null
  heure_bascule: number | null
  /** Les modèles proposés par le serveur (« Remettre les modèles proposés »). */
  modeles_defaut: ModeleTarif[] | null
  ecriture: Ecriture | null
  actions: Actions | null
}

function lireReponsePreferences(v: unknown): ReponsePreferences | null {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return null
  const o = v as Record<string, unknown>
  const ms = o['mode_sur'] as { libelle?: unknown; message?: unknown } | null | undefined
  const defauts = o['defauts'] as { modeles_tarifs?: unknown } | null | undefined
  return {
    preferences: lirePreferences(v), gel_illisible: o['gel_illisible'] === true,
    maj_a: typeof o['maj_a'] === 'string' ? o['maj_a'] : null,
    mode_sur: ms && typeof ms.libelle === 'string' ? { libelle: ms.libelle, message: typeof ms.message === 'string' ? ms.message : null } : null,
    heure_bascule: typeof o['heure_bascule'] === 'number' ? o['heure_bascule'] : null,
    modeles_defaut: defauts && Array.isArray(defauts.modeles_tarifs) ? lirePreferences({ modeles_tarifs: defauts.modeles_tarifs }).modeles_tarifs : null,
    ecriture: 'ecriture' in o ? lireEcriture(o['ecriture']) : null, actions: '_actions' in o ? lireActions(o['_actions']) : null,
  }
}

export function chargerPreferences(): Promise<Resultat<ReponsePreferences>> {
  return lire(`${BASE}/preferences`, lireReponsePreferences)
}

/** L'identité de la session : les clés d'idempotence sont rangées PAR PERSONNE (§3.7). */
export async function chargerMoi(): Promise<{ id: string } | null> {
  try {
    const r = await apiRead<unknown>('/api/v1/me', OPTIONS)
    const u = (r.data as { user?: { id?: unknown } } | null)?.user
    return r.status === 200 && typeof u?.id === 'string' ? { id: u.id } : null
  } catch { return null }
}

/* ═══════════════════════ écritures ═══════════════════════ */

export interface Creation { soiree_id: string; version: number; rejouee: boolean }

export function creerSoiree(corps: CorpsCreation, cle: string): Promise<Resultat<Creation>> {
  return ecrire('POST', `${BASE}/soirees`, corps, cle, (v) => {
    const o = v as { soiree_id?: unknown; soiree?: { version?: unknown } | null; version?: unknown; rejouee?: unknown } | null
    if (!o || typeof o.soiree_id !== 'string' || !UUID.test(o.soiree_id)) return null
    const version = typeof o.soiree?.version === 'number' ? o.soiree.version : typeof o.version === 'number' ? o.version : 1
    return { soiree_id: o.soiree_id.toLowerCase(), version, rejouee: o.rejouee === true }
  })
}

export interface CorpsSoiree {
  version: number
  nom: string; nuit: string; ouverture: string; fermeture: string; jauge_totale: number
  salles: { code: string; jauge: number | null }[]; famille: string | null; presentation: string | null
}

export function modifierSoiree(soireeId: string, corps: CorpsSoiree, cle: string): Promise<Resultat<Fiche>> {
  const s = id(soireeId)
  return s ? ecrire('PUT', `${BASE}/soirees/${s}`, corps, cle, lireFiche) : Promise.resolve(introuvable())
}

export type GesteSoiree = 'publier' | 'programmer' | 'brouillon' | 'annuler' | 'arreter-ventes'

export function gesteSoiree(soireeId: string, geste: GesteSoiree, corps: Record<string, unknown>, cle: string): Promise<Resultat<Fiche>> {
  const s = id(soireeId)
  return s ? ecrire('POST', `${BASE}/soirees/${s}/${geste}`, corps, cle, lireFiche) : Promise.resolve(introuvable())
}

export function ajouterTarif(soireeId: string, corps: CorpsTarif, cle: string): Promise<Resultat<Fiche>> {
  const s = id(soireeId)
  return s ? ecrire('POST', `${BASE}/soirees/${s}/tarifs`, corps, cle, lireFiche) : Promise.resolve(introuvable())
}

export function modifierTarif(soireeId: string, tarifId: string, corps: CorpsTarif & { version: number }, cle: string): Promise<Resultat<Fiche>> {
  const s = id(soireeId); const t = id(tarifId)
  return s && t ? ecrire('PUT', `${BASE}/soirees/${s}/tarifs/${t}`, corps, cle, lireFiche) : Promise.resolve(introuvable())
}

export type GesteTarif = 'retirer' | 'remettre' | 'supprimer'

export function gesteTarif(soireeId: string, tarifId: string, geste: GesteTarif, version: number, cle: string): Promise<Resultat<Fiche>> {
  const s = id(soireeId); const t = id(tarifId)
  return s && t ? ecrire('POST', `${BASE}/soirees/${s}/tarifs/${t}/${geste}`, { version }, cle, lireFiche) : Promise.resolve(introuvable())
}

export function ecrireReglages(changements: ChangementReglage[], cle: string): Promise<Resultat<true>> {
  return ecrire('PUT', `${BASE}/reglages`, { changements }, cle, () => true)
}

/** Un correctif partiel (les seules clés changées), avec l'heure de la ligne lue par l'écran. */
export function ecrirePreferences(p: Partial<CorpsPreferences> & { maj_a_lue: string | null }, cle: string): Promise<Resultat<ReponsePreferences>> {
  return ecrire('PUT', `${BASE}/preferences`, p, cle, lireReponsePreferences)
}

/* ═══════════════════════ l'affiche (§9.5) ═══════════════════════ */

/**
 * Ce que rend une écriture de l'affiche : la réponse habituelle des écritures
 * (la fiche relue, `soiree.affiche` compris), avec `resultat: { affiche,
 * rejouee, inchange? }` — l'affiche posée, ou `null` après un retrait. Lue avec
 * la même prudence que le reste : une affiche illisible vaut « pas d'affiche ».
 */
export interface ReponseAffiche { affiche: Affiche | null; fiche: Fiche | null; rejouee: boolean; inchange: boolean }

function lireReponseAffiche(v: unknown): ReponseAffiche | null {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return null
  const o = v as Record<string, unknown>
  const r = typeof o['resultat'] === 'object' && o['resultat'] !== null ? (o['resultat'] as Record<string, unknown>) : null
  const fiche = 'soiree' in o || 'relecture' in o ? lireFiche(v) : null
  if (!r && !fiche) return null
  const affiche = r && 'affiche' in r ? lireAffiche(r['affiche']) : fiche?.soiree?.affiche ?? null
  return { affiche, fiche, rejouee: o['rejouee'] === true || r?.['rejouee'] === true, inchange: r?.['inchange'] === true }
}

/**
 * Poser (ou remplacer) l'affiche : un corps BINAIRE (JPEG, PNG ou WebP, 8 Mio
 * au plus), par `adminUpload` — mêmes garanties que les autres écritures :
 * jeton CSRF, reprise après confirmation d'identité, clé d'idempotence.
 * `versionAffiche` : la version LUE (`null` = « je n'ai vu aucune affiche ») ;
 * une autre version posée entre-temps est refusée (`VERSION_PERIMEE`).
 */
export async function envoyerAffiche(soireeId: string, fichier: File, versionAffiche: number | null, cle: string): Promise<Resultat<ReponseAffiche>> {
  const s = id(soireeId)
  if (!s) return introuvable()
  const version = versionAffiche === null ? 'aucune' : String(versionAffiche)
  try {
    const r = await adminUpload<{ error?: string }>('POST', `${BASE}/soirees/${s}/affiche?version_affiche=${version}`, fichier,
      { ...OPTIONS, headers: { 'Idempotency-Key': cle } })
    const e = lireErreur(r.status, r.data)
    if (e) return { ok: false, erreur: e }
    const v = lireReponseAffiche(r.data)
    return v === null ? formeInattendue(r.status, true) : { ok: true, status: r.status, data: v }
  } catch {
    return { ok: false, erreur: erreurReseau(true) }
  }
}

export function retirerAffiche(soireeId: string, versionAffiche: number, cle: string): Promise<Resultat<ReponseAffiche>> {
  const s = id(soireeId)
  return s ? ecrire('POST', `${BASE}/soirees/${s}/affiche/retirer`, { version_affiche: versionAffiche }, cle, lireReponseAffiche) : Promise.resolve(introuvable())
}
